import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';

const OTP_LENGTH = 6;

/**
 * Six-box OTP input.
 *
 * Props:
 *   digits    – string[6] controlled array
 *   onChange  – (string[6]) => void
 *   hasError  – boolean  (red border + glow)
 *   autoFocus – boolean  (focus first box on mount / when cleared)
 */
const OtpInput = ({ digits, onChange, hasError, autoFocus = true }) => {
  const refs = useRef([]);

  const focus = (i) => refs.current[i]?.focus();

  useEffect(() => {
    if (!autoFocus) return undefined;
    const id = requestAnimationFrame(() => focus(0));
    return () => cancelAnimationFrame(id);
  }, [autoFocus]);

  useEffect(() => {
    if (!autoFocus) return undefined;
    if (digits.every((d) => !d)) {
      const id = requestAnimationFrame(() => focus(0));
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [digits, autoFocus]);

  const handleKey = (e, i) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i]) {
        onChange(digits.map((d, idx) => (idx === i ? '' : d)));
      } else if (i > 0) {
        onChange(digits.map((d, idx) => (idx === i - 1 ? '' : d)));
        focus(i - 1);
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      focus(i - 1);
    } else if (e.key === 'ArrowRight' && i < OTP_LENGTH - 1) {
      focus(i + 1);
    }
  };

  const handleChange = (e, i) => {
    const val = e.target.value.replace(/\D/g, '');
    if (!val) return;
    const char = val[val.length - 1];
    onChange(digits.map((d, idx) => (idx === i ? char : d)));
    if (i < OTP_LENGTH - 1) focus(i + 1);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!text) return;
    onChange(Array(OTP_LENGTH).fill('').map((_, i) => text[i] || ''));
    focus(Math.min(text.length, OTP_LENGTH - 1));
  };

  return (
    <Box display="flex" justifyContent="center" gap={1.2} my={3} dir="ltr">
      {digits.map((d, i) => (
        <Box
          key={i}
          component="input"
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKey(e, i)}
          onPaste={handlePaste}
          autoFocus={autoFocus && i === 0}
          onFocus={(e) => e.target.select()}
          sx={{
            width: 48,
            height: 56,
            textAlign: 'center',
            fontSize: '1.5rem',
            fontWeight: 600,
            fontFamily: 'inherit',
            border: '2px solid',
            borderColor: hasError ? 'error.main' : (d ? 'primary.main' : 'divider'),
            borderRadius: 2,
            bgcolor: 'background.paper',
            color: hasError ? 'error.main' : 'text.primary',
            outline: 'none',
            cursor: 'text',
            transition: 'border-color 0.15s',
            '&:focus': {
              borderColor: hasError ? 'error.main' : 'primary.main',
              boxShadow: hasError
                ? '0 0 0 3px rgba(211,47,47,0.15)'
                : '0 0 0 3px rgba(25,118,210,0.15)',
            },
          }}
        />
      ))}
    </Box>
  );
};

export default OtpInput;
